import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { type ActivatedRouteSnapshot, type CanDeactivateFn } from '@angular/router';
import { AlertService } from '@modules/alert/services/alert.service';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { firstValueFrom, Subject } from 'rxjs';

/** Routes and workbook-id changes ask the live editor before its grid is destroyed. */
@Injectable({ providedIn: 'root' })
export class SheetsEditorNavigationService {
  private readonly alerts = inject(AlertService);
  private active: { id: string; confirm: () => Promise<boolean> } | null = null;
  private pending: Promise<boolean> | null = null;

  public register(id: string, confirm: () => Promise<boolean>): () => void {
    const entry = { id, confirm };
    this.active = entry;
    return () => {
      if (this.active === entry) this.active = null;
    };
  }

  public canLeave(nextId?: string): Promise<boolean> {
    if (!this.active || (nextId !== undefined && nextId === this.active.id)) return Promise.resolve(true);
    return this.active.confirm();
  }

  /** Stay is the default on close/Escape. Leaving requires an explicit button press. */
  public confirmUnsaved(): Promise<boolean> {
    if (this.pending) return this.pending;
    const closed = new Subject<void>();
    this.pending = new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (leave: boolean): void => {
        if (settled) return;
        settled = true;
        closed.next();
        closed.complete();
        resolve(leave);
      };
      this.alerts.show({
        title: SHEETS_STRINGS['Unsaved workbook changes'],
        body: SHEETS_STRINGS['Some workbook changes have not been saved. Stay to resolve them, or leave and risk losing them.'],
        appearance: 'prompt',
        showUntil: closed,
        rightButtons: [
          { title: SHEETS_STRINGS.Stay, appearance: 'secondary', close: true, click: () => finish(false) },
          { title: SHEETS_STRINGS['Leave anyway'], appearance: 'primary', close: true, click: () => finish(true) },
        ],
        onClose: () => finish(false),
      });
    }).finally(() => {
      this.pending = null;
    });
    return this.pending;
  }
}

/**
 * The editor route's guard. With `runGuardsAndResolvers: 'always'` it runs on every navigation that keeps the
 * route too (`?id=…&sheet=…`, another workbook's id), so it asks only when the workbook changes: the same
 * id, a sheet change, passes. One guard, so a navigation asks once; a canActivate as well asked again after
 * "Leave anyway" on a workbook switch.
 *
 * Then the page's item form (SBUX-1036: the editor's Details tab edits the workbook's title, description, folder), as
 * the detail view asks before it lets a workbook go: unsaved there, it offers to save them, or to discard them.
 */
export const sheetsEditorCanDeactivate: CanDeactivateFn<unknown> = (_component, route, _current, next) => {
  const stays = (node: ActivatedRouteSnapshot): boolean =>
    node.routeConfig === route.routeConfig || node.children.some(stays);
  const nextId = stays(next.root) ? (next.root.queryParamMap.get('id') ?? '') : undefined;
  const id = route.queryParamMap.get('id') ?? '';
  // Looked up only when the workbook is going: its prompts need the app's alerts and toasts.
  const injector = inject(EnvironmentInjector);
  return inject(SheetsEditorNavigationService)
    .canLeave(nextId)
    .then((leave) =>
      leave && nextId !== id && id
        ? firstValueFrom(injector.get(FormChangesModalsService).confirmUnsavedChanges(id), { defaultValue: true })
        : leave,
    );
};
